import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { UpgradePlanDialogs, type UpgradePlanCardType } from './UpgradePlanDialogs';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { proPricingPlansStore } from '~/lib/stores/pricing/proPlans';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { userStore } from '~/lib/stores/user';
import type { EffectiveTier } from '~/utils/effectiveTier';
import { type Pricing } from '~/utils/pricing';

interface Props {
  className?: string;
}

export const OutOfTokensAlert = ({ className }: Props) => {
  const activeOrganization = useStore(userStore.getActiveOrganization());

  const tier = useStore(userStore.getTierStore());

  const nextPersonalTier = useStore(proPricingPlansStore.nextHigherTierPricing);
  const nextTeamTier = useStore(teamPricingPlansStore.nextHigherTierPricing);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(true);

  const canUpgrade = canUpgradeToNextTier({ tier, nextPersonalTier, nextTeamTier });

  if (!canUpgrade) {
    /**
     * We display simple alert with no actions, because you have nothing to do.
     * You have no higher plan to upgrade to, so the only thing you can do is wait until tokens are refilled next month.
     */

    return (
      <div className="pt-4">
        <Alert title="You have hit the rate limit" type="warning" direction="row" className={className}>
          You will be able to continue chatting after the next month's token refill.
        </Alert>
      </div>
    );
  }

  const isFreePlan = !tier || tier?.plan === 'personal';
  const canSeeUpgrade = tier?.plan !== 'teams' || (tier?.plan === 'teams' && activeOrganization?.role === 'admin');
  const title = isFreePlan ? 'You’ve hit today’s free limit' : "You're out of tokens";

  return (
    <div className="pt-4">
      <Alert
        title={title}
        actions={
          canSeeUpgrade && (
            <Button size="sm" variant="secondary" onClick={() => setIsUpgradeModalOpen(true)}>
              Upgrade
            </Button>
          )
        }
        direction="row"
        type="info"
        className={className}
      >
        {isFreePlan && 'Come back tomorrow for more, or upgrade to keep going without interruptions.'}

        {tier?.plan === 'pro' && 'Upgrade now to keep going, or continue coding for free in the editor.'}

        {tier?.plan === 'teams' &&
          activeOrganization?.role === 'admin' &&
          'Please upgrade your team plan to keep building, or you can continue coding for free in the editor.'}

        {tier?.plan === 'teams' &&
          activeOrganization?.role !== 'admin' &&
          'Please contact your team admin to consider upgrading your plan.'}
      </Alert>

      <UpgradePlanDialogs
        isOpen={isUpgradeModalOpen}
        cards={['teams', ...(!activeOrganization ? ['pro' as UpgradePlanCardType] : [])]}
        onClose={() => setIsUpgradeModalOpen(false)}
        message="You just ran out of tokens, upgrade your current plan to keep on building."
      />
    </div>
  );
};

function canUpgradeToNextTier({
  tier,
  nextPersonalTier,
  nextTeamTier,
}: {
  tier: EffectiveTier | undefined;
  nextPersonalTier: Pricing | null | undefined;
  nextTeamTier: Pricing | null | undefined;
}) {
  if (!tier) {
    return false;
  }

  if (tier.plan === 'personal') {
    return true;
  }

  if (tier.plan === 'pro') {
    return !!nextPersonalTier;
  }

  if (tier.plan === 'teams') {
    if (tier.level === 0) {
      // if the team is unpaid, they can upgrade to the first team tier
      return true;
    }

    return !!nextTeamTier;
  }

  return false;
}
